import type {Incoming, SSOdMessage} from '@pexip/infinity';
import {createRegistration, createRegistrationSignals} from '@pexip/infinity';

import {applicationConfig} from '../applicationConfig';
import {config} from '../config';
import {logger} from '../logger';
import {
    registrationErrorSignal,
    registrationExpiredSignal,
    registrationIdpsSignal,
    registrationIncomingCallSignal,
    registrationMissedCallSignal,
    registrationStatusSignal,
    registrationStepSignal,
} from '../signals/Registration.signals';
import type {Idp, RegistrationStatus, RegistrationStep} from '../types';
import {RegistrationError} from '../types';

import {
    type CallDestination,
    createHttpsOrigin,
    resolveCallDestination,
    type ServiceDiscovery,
} from './ServiceDiscovery.service';

export interface Registrar {
    node?: string;
    host?: string;
}

interface RegistrationProps {
    status: RegistrationStatus;
    step: RegistrationStep;
    instance: ReturnType<typeof createRegistration> | undefined;
    deviceAlias: string;
    registrar: Registrar | undefined;
    routeViaRegistrar: boolean;
    idps: Idp[];
    error: RegistrationError | undefined;
    isExpired: boolean;
    incomingCall: Incoming | undefined;
    incomingCalls: Map<string, Incoming>;
}

const isIdpListResult = (
    result: unknown,
): result is {
    idp: Array<{
        name: string;
        uuid: string;
        img?: string;
        disable_popup_flow?: boolean;
    }>;
} =>
    typeof result === 'object' &&
    result !== null &&
    'idp' in result &&
    Array.isArray((result as {idp: unknown}).idp);

const isRedirectResult = (
    result: unknown,
): result is {
    redirect_url: string;
    redirect_idp: {name: string; uuid: string};
    disable_popup_flow?: boolean;
} => typeof result === 'object' && result !== null && 'redirect_url' in result;

/**
 * Creates a registration service that manages the registration lifecycle.
 *
 * Wraps `createRegistration` from `@pexip/infinity` and exposes registration
 * state via signals.
 *
 * @example
 * ```ts
 * const result = await registrationService.register({deviceAlias: 'user@host.pexip.com'});
 * ```
 */
export const createRegistrationService = () => {
    const registrationSignals = createRegistrationSignals();
    const props: RegistrationProps = {
        status: 'idle',
        step: null,
        instance: undefined,
        deviceAlias: '',
        registrar: undefined,
        routeViaRegistrar: false,
        idps: [],
        error: undefined,
        isExpired: false,
        incomingCall: undefined,
        incomingCalls: new Map(),
    };

    let serviceDiscovery: ServiceDiscovery | undefined;
    let detachIncoming: (() => void) | undefined;
    let detachIncomingCancelled: (() => void) | undefined;

    /**
     * Native builds resolve the device alias exactly like a call destination,
     * except that token exchange must reach the node that issued the session.
     */
    const getRegistrar = async (
        exchangeToken?: boolean,
    ): Promise<Registrar> => {
        if (!serviceDiscovery) {
            return {node: applicationConfig.node};
        }
        const node = config.get('registrarNode');
        if (exchangeToken && node) {
            return {node, host: createHttpsOrigin(node)};
        }
        const resolved = await resolveCallDestination<CallDestination>({
            callArgs: {conferenceAlias: props.deviceAlias},
            serviceDiscovery,
        });
        return {node: resolved.node, host: resolved.callArgs.host};
    };

    const setStatus = (newStatus: RegistrationStatus) => {
        if (props.status === newStatus) {
            return;
        }
        props.status = newStatus;
        logger.debug(
            {context: 'Registration'},
            `Status changed to ${newStatus}`,
        );
        registrationStatusSignal.emit(newStatus);
    };

    const setStep = (newStep: RegistrationStep) => {
        if (props.step === newStep) {
            return;
        }
        props.step = newStep;
        logger.debug(
            {context: 'Registration'},
            `Step changed to ${String(newStep)}`,
        );
        registrationStepSignal.emit(newStep);
    };

    const setError = (newError: RegistrationError | undefined) => {
        if (props.error === newError) {
            return;
        }
        props.error = newError;
        registrationErrorSignal.emit(newError);
    };

    const setExpired = (isExpired: boolean) => {
        if (props.isExpired === isExpired) {
            return;
        }
        props.isExpired = isExpired;
        registrationExpiredSignal.emit(isExpired);
    };

    const setIdps = (newIdps: Idp[]) => {
        props.idps = newIdps;
        registrationIdpsSignal.emit(newIdps);
    };

    /**
     * Reset the registration modal state to idle.
     *
     * @example
     * ```ts
     * registrationService.reset();
     * ```
     */
    const reset = () => {
        setStep(null);
        setError(undefined);
    };

    /**
     * Register with a video address. Handles the full registration flow
     * including IDP discovery and SSO redirects.
     *
     * @example
     * ```ts
     * const result = await registrationService.register({deviceAlias: 'user@host.pexip.com'});
     * if (result.type === 'idps') { // show IDP picker }
     * ```
     */
    const register = async ({
        deviceAlias,
        ssoToken,
        chosenIdp,
        username,
        password,
        exchangeToken,
    }: {
        deviceAlias?: string;
        ssoToken?: string;
        chosenIdp?: string;
        username?: string;
        password?: string;
        exchangeToken?: boolean;
    }) => {
        if (deviceAlias) {
            props.deviceAlias = deviceAlias;
            props.registrar = undefined;
        }

        if (!props.deviceAlias) {
            throw new Error('No alias provided for registration');
        }

        setError(undefined);
        setStatus('registering');

        try {
            props.registrar ??= await getRegistrar(exchangeToken);
        } catch (error) {
            setError(RegistrationError.UnexpectedError);
            setStep('register');
            setStatus('error');
            logger.error(
                {context: 'Registration', error},
                'Registrar discovery error',
            );
            return {type: 'error' as const, error: props.error};
        }

        detachIncoming?.();
        detachIncomingCancelled?.();
        props.instance = createRegistration({
            ...props.registrar,
            registrationSignals,
        });
        // FIXME: we should handle stacked incoming calls instead of overriding the previous one
        detachIncoming = registrationSignals.onIncoming.add(incoming => {
            props.incomingCalls.set(incoming.token, incoming);
            props.incomingCall = incoming;
            registrationIncomingCallSignal.emit(incoming);
        });
        detachIncomingCancelled = registrationSignals.onIncomingCancelled.add(
            ({token}) => {
                const cancelled = props.incomingCalls.get(token);
                if (cancelled) {
                    registrationMissedCallSignal.emit(cancelled);
                }
                if (props.incomingCall?.token === token) {
                    props.incomingCall = undefined;
                    registrationIncomingCallSignal.emit(undefined);
                }
            },
        );

        try {
            const response = await props.instance.register({
                alias: props.deviceAlias,
                ssoToken,
                chosenIdp,
                username,
                password,
                exchangeToken,
            });

            switch (response.status) {
                case 200: {
                    setExpired(false);
                    setStatus('registered');
                    const displayName =
                        response.data.result.display_name ||
                        config.get('displayName');
                    if (displayName) {
                        config.set({
                            key: 'displayName',
                            value: displayName,
                            persist: true,
                        });
                        config.set({
                            key: 'skipDisplayNamePrompt',
                            value: true,
                        });
                    }
                    config.set({
                        key: 'deviceAlias',
                        value: props.deviceAlias,
                        persist: true,
                    });
                    if (serviceDiscovery) {
                        config.set({
                            key: 'registrarNode',
                            value: props.registrar?.node ?? '',
                            persist: true,
                        });
                    }
                    props.routeViaRegistrar =
                        response.data.result.route_via_registrar;
                    return {type: 'success' as const};
                }

                case 401:
                    setError(RegistrationError.InvalidVideoAddress);
                    setStep('register');
                    setStatus('error');
                    logger.error(
                        {context: 'Registration'},
                        `Registration failed with status ${response.status}`,
                    );
                    return {type: 'error' as const, error: props.error};

                case 403: {
                    const result = response.data.result;
                    if (isIdpListResult(result)) {
                        const idps: Idp[] = result.idp.map(
                            ({disable_popup_flow, ...rest}) => ({
                                ...rest,
                                disablePopupFlow: disable_popup_flow,
                            }),
                        );
                        setIdps(idps);
                        setStep('select-idp');
                        setStatus('idle');
                        return {type: 'idps' as const, idps};
                    }

                    if (isRedirectResult(result)) {
                        setStatus('idle');
                        return {
                            type: 'redirect' as const,
                            redirectUrl: result.redirect_url,
                            redirectIdp: result.redirect_idp,
                            disablePopupFlow: result.disable_popup_flow,
                        };
                    }

                    setError(RegistrationError.AuthFailed);
                    setStep('register');
                    setStatus('error');
                    return {type: 'error' as const, error: props.error};
                }

                default:
                    setError(RegistrationError.RegistrationFailed);
                    setStep('register');
                    setStatus('error');
                    logger.error(
                        {context: 'Registration'},
                        `Registration failed with status ${response.status}`,
                    );
                    return {type: 'error' as const, error: props.error};
            }
        } catch (error) {
            setError(RegistrationError.UnexpectedError);
            setStep('register');
            setStatus('error');
            logger.error(
                {context: 'Registration', error},
                'Registration error',
            );
            return {type: 'error' as const, error: props.error};
        }
    };

    /**
     * Handle an SSO result from the popup window. Processes the token on
     * success or sets the appropriate error state on failure.
     */
    const handleSsoResult = async (message: SSOdMessage) => {
        switch (message.result) {
            case 'success': {
                await register({ssoToken: message.token});
                break;
            }
            case 'forbidden':
                logger.warn(
                    {context: 'Registration'},
                    'SSO auth failed for registration',
                );
                setError(RegistrationError.SsoAuthFailed);
                setStep('register');
                break;
            case 'service unavailable':
                logger.warn(
                    {context: 'Registration'},
                    'SSO auth failed for registration: maintenance mode',
                );
                setError(RegistrationError.SsoUnavailable);
                setStep('register');
                break;
        }
    };

    /**
     * Unregister the current registration and clean up resources.
     *
     * @example
     * ```ts
     * await registrationService.unregister();
     * ```
     */
    const unregister = async () => {
        detachIncoming?.();
        detachIncoming = undefined;
        detachIncomingCancelled?.();
        detachIncomingCancelled = undefined;
        props.incomingCall = undefined;
        props.incomingCalls.clear();
        await props.instance?.unregister();
        props.instance = undefined;
        config.set({key: 'deviceAlias', value: '', persist: true});
        config.set({key: 'displayName', value: '', persist: true});
        config.set({key: 'registrarNode', value: '', persist: true});
        props.deviceAlias = '';
        props.registrar = undefined;
        props.routeViaRegistrar = false;
        setIdps([]);
        setError(undefined);
        setExpired(false);
        setStep(null);
        setStatus('idle');
    };

    /**
     * Search the directory for registered users.
     *
     * @example
     * ```ts
     * const result = await registrationService.directorySearch('john');
     * ```
     */
    const directorySearch = async (query: string) => {
        if (!props.instance) {
            throw new Error('Not registered');
        }
        return props.instance.directorySearch({query});
    };

    return {
        register,
        handleSsoResult,
        unregister,
        reset,
        directorySearch,
        setStep,
        setError,
        setExpired,
        setIdps,
        /**
         * Enables native registrar discovery; omit it to keep the browser
         * application node.
         */
        setServiceDiscovery: (discovery: ServiceDiscovery | undefined) => {
            serviceDiscovery = discovery;
        },
        /** Registrar that must carry calls when the registration routes via it. */
        getCallRegistrar: () =>
            props.status === 'registered' && props.routeViaRegistrar
                ? props.registrar
                : undefined,
        getStatus: () => props.status,
        getStep: () => props.step,
        getIdps: () => props.idps,
        getError: () => props.error,
        getIsExpired: () => props.isExpired,
        getAlias: () => props.deviceAlias,
        getRegistrationToken: () => props.instance?.registrationToken,
        getIncomingCall: (token?: string) =>
            token ? props.incomingCalls.get(token) : props.incomingCall,
        dismissIncomingCall: () => {
            props.incomingCall = undefined;
            registrationIncomingCallSignal.emit(undefined);
        },
        declineIncomingCall: async () => {
            const incoming = props.incomingCall;
            props.incomingCall = undefined;
            registrationIncomingCallSignal.emit(undefined);
            if (!incoming) {
                return;
            }
            try {
                await props.instance?.declineIncomingCall(incoming);
            } catch (error) {
                logger.error(
                    {context: 'Registration', error},
                    'Failed to release incoming call token',
                );
            }
        },
    };
};

export const registrationService = createRegistrationService();

export type RegistrationService = ReturnType<typeof createRegistrationService>;
